/**
 * AdminQaValidation (/admin/qa-validation, "Policy validation" in the nav):
 * the shared checklist for confirming each Best Practice policy behaves as
 * written, on mockup carbon/01's brief and carbon/02's tables.
 *
 *   1. The brief (BriefHero): what to test next (failing, then flagged, then
 *      untested policies), its primary button filtering the list to them,
 *      and Export CSV beside it.
 *   2. Four tiles: tested (with a progress bar), pass, fail, investigate.
 *   3. The policies: one DataTable with a toolbar (search, product,
 *      criticality, status, Refresh) and a collapsible group per product.
 *      Each row sets a status with selectable tags (paused while it saves),
 *      takes notes in place, and expands into the policy's description,
 *      remediation and references.
 *
 * Criticality reads as the severity glyph and word (Critical, High, Medium),
 * never the SHALL / SHOULD / MAY keywords; status reads as a word on a
 * neutral tag.
 */
import { useState, useMemo, type ReactNode } from 'react'
import { useMsal } from '@azure/msal-react'
import toast from 'react-hot-toast'
import { useQaValidation, useUpsertQaValidation } from '@/hooks/useQaValidation'
import type { QaPolicy, QaStatus, UpsertQaValidationRequest } from '@/types/qaValidation'
import type { ActionBrief } from '@/lib/brief-shape'
import EmptyState from '@/components/shared/EmptyState'
import { BigNumber } from '@/components/carbon/BigNumber'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { CodeSnippet } from '@/components/carbon/CodeSnippet'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { DataTable, type DataColumn } from '@/components/carbon/DataTable'
import { IconArrowRight, IconDownload, IconExternal } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { ProgressBar } from '@/components/carbon/ProgressBar'
import { RowDetail, RowDetailSection } from '@/components/carbon/RowDetail'
import { SEVERITY_WORD, severityFromApi } from '@/components/carbon/severity'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Spinner } from '@/components/carbon/Spinner'
import { TableSearch } from '@/components/carbon/TableSearch'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { TextArea } from '@/components/carbon/TextArea'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { composeQaBrief } from '@/components/AdminPortal/page-briefs'

const PRODUCTS = ['EntraID', 'Defender', 'Exchange', 'SharePoint', 'OneDrive', 'Teams', 'Intune'] as const

/** The criticality keywords the policies carry; the UI shows their severity word. */
const CRITICALITIES = ['SHALL', 'SHOULD', 'MAY'] as const

function downloadCsv(policies: QaPolicy[]) {
  const header = 'PolicyId,Title,Product,Category,Criticality,Status,TestedBy,TestedAt,Notes'
  const rows = policies.map(p =>
    [
      p.policyId,
      `"${(p.title ?? '').replace(/"/g, '""')}"`,
      p.product ?? '',
      p.category ?? '',
      p.criticality,
      p.status,
      p.testedBy ?? '',
      p.testedAt ?? '',
      `"${(p.notes ?? '').replace(/"/g, '""')}"`,
    ].join(',')
  )
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `policy-validation-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '…'
}

/** The criticality as the severity glyph and its word; an unknown value reads as sent. */
function Criticality({ value }: { value: string }) {
  if (!(CRITICALITIES as readonly string[]).includes(value)) return <>{value}</>
  const level = severityFromApi(value)
  return <SeverityGlyph level={level}>{SEVERITY_WORD[level]}</SeverityGlyph>
}

interface NotesCellProps {
  policy: QaPolicy
  localNotes: string
  notesOpen: boolean
  onOpenNotes: () => void
  onChangeNotes: (value: string) => void
  onBlurNotes: () => void
}

function NotesCell({ policy, localNotes, notesOpen, onOpenNotes, onChangeNotes, onBlurNotes }: NotesCellProps) {
  if (policy.status === 'Untested') {
    return <span className="text-helper text-text-helper">Set status first</span>
  }

  if (notesOpen) {
    return (
      <TextArea
        label={`Notes for ${policy.policyId}`}
        hideLabel
        value={localNotes}
        onChange={e => onChangeNotes(e.target.value)}
        rows={2}
        maxLength={2000}
        onBlur={onBlurNotes}
        autoFocus
        placeholder="Add notes"
        className="min-w-[12rem] resize-none text-helper"
      />
    )
  }

  return (
    <Button kind="ghost" size="sm" onClick={onOpenNotes} className="-ml-3 max-w-full text-left" aria-label={`Notes for ${policy.policyId}`}>
      {policy.notes ? truncate(policy.notes, 60) : 'Add notes'}
    </Button>
  )
}

const GROUP_BADGES: Array<{ status: QaStatus; label: string }> = [
  { status: 'Pass', label: 'Pass' },
  { status: 'Fail', label: 'Fail' },
  { status: 'Untested', label: 'Untested' },
]

function GroupStatusBadges({ policies }: { policies: QaPolicy[] }) {
  return (
    <>
      {GROUP_BADGES.map(({ status, label }) => {
        const count = policies.filter(p => p.status === status).length
        if (count === 0) return null
        return (
          <Tag key={status} kind={status === 'Fail' ? 'high-contrast' : 'default'} size="sm">
            {count} {label}
          </Tag>
        )
      })}
    </>
  )
}

const hasDetail = (p: QaPolicy) => !!(p.description || p.remediationSteps || p.powerShellEquivalent || p.remediationUrl)

/** The group a policy sits in: its product. */
const productOf = (p: QaPolicy) => p.product ?? 'Other'

/**
 * The policies as one DataTable grouped by product: each group row carries
 * the product, its count and status tags, and opens and closes from the
 * page's state (`expandedGroups`). The rows' status and notes edits live
 * here, keyed by policy, through one shared mutation; each row's controls
 * are disabled only while its own save runs.
 */
function PolicyChecklist({
  policies,
  testedByName,
  expandedGroups,
  onExpandedGroupsChange,
  toolbar,
  isLoading,
}: {
  /** Already filtered, in product order. */
  policies: QaPolicy[]
  testedByName: string
  expandedGroups: string[]
  onExpandedGroupsChange: (products: string[]) => void
  toolbar: ReactNode
  isLoading: boolean
}) {
  const mutation = useUpsertQaValidation()
  const [notesOpenId, setNotesOpenId] = useState<string | null>(null)
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({})
  const [pending, setPending] = useState<Set<string>>(new Set())

  // A row's notes are its draft while the notes field is open, and the server's notes otherwise, so a
  // closed row always follows the server data (e.g., another user updated it).
  const localNotesOf = (policy: QaPolicy) =>
    notesOpenId === policy.policyEntityId ? notesDraft[policy.policyEntityId] ?? policy.notes ?? '' : policy.notes ?? ''

  const track = (id: string, on: boolean) =>
    setPending(prev => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  // One promise per save (mutateAsync): callbacks passed to `mutate` fire only for the latest call, so a
  // second row saved before the first settled would leave the first row busy for good.
  const save = (policy: QaPolicy, body: UpsertQaValidationRequest, failure: string, onSaved?: () => void) => {
    track(policy.policyEntityId, true)
    mutation
      .mutateAsync({ policyEntityId: policy.policyEntityId, body })
      .then(() => onSaved?.())
      .catch(() => toast.error(failure))
      .finally(() => track(policy.policyEntityId, false))
  }

  const handleStatusChange = (policy: QaPolicy, status: 'Pass' | 'Fail' | 'Investigate' | 'Untested') => {
    if (pending.has(policy.policyEntityId)) return
    save(
      policy,
      { status, notes: localNotesOf(policy) || undefined, testedBy: testedByName },
      `Failed to save ${policy.policyId}`,
      () => toast.success(`${policy.policyId} ${status === 'Untested' ? 'reset' : 'marked ' + status}`),
    )
  }

  const handleNotesBlur = (policy: QaPolicy) => {
    const localNotes = localNotesOf(policy)
    setNotesOpenId(null)
    if (localNotes === (policy.notes ?? '')) return
    if (policy.status === 'Untested') return
    save(
      policy,
      { status: policy.status as 'Pass' | 'Fail' | 'Investigate', notes: localNotes || undefined, testedBy: testedByName },
      'Failed to save notes',
    )
  }

  const columns: DataColumn<QaPolicy>[] = [
    {
      id: 'policyId',
      header: 'Policy ID',
      nowrap: true,
      width: '9rem',
      cell: p => <span className="font-mono text-helper text-text-secondary">{p.policyId}</span>,
    },
    { id: 'title', header: 'Title', primary: true, cell: p => p.title },
    { id: 'criticality', header: 'Criticality', nowrap: true, cell: p => <Criticality value={p.criticality} /> },
    {
      id: 'status',
      header: 'Status',
      cell: p => {
        const busy = pending.has(p.policyEntityId)
        return (
          <div role="group" aria-label={`Status of ${p.policyId}`} className="flex flex-wrap items-center gap-1" aria-busy={busy || undefined}>
            {(['Pass', 'Fail', 'Investigate'] as const).map(s => (
              <Tag key={s} selected={p.status === s} disabled={busy} onClick={() => handleStatusChange(p, s)}>
                {s}
              </Tag>
            ))}
            {p.status !== 'Untested' && (
              <Button
                kind="ghost"
                size="sm"
                onClick={() => handleStatusChange(p, 'Untested')}
                disabled={busy}
                title="Reset status (keeps notes)"
              >
                Reset
              </Button>
            )}
            {busy && <Spinner label={`Saving ${p.policyId}`} />}
          </div>
        )
      },
    },
    {
      id: 'notes',
      header: 'Notes',
      cell: p => (
        <NotesCell
          policy={p}
          localNotes={localNotesOf(p)}
          notesOpen={notesOpenId === p.policyEntityId}
          onOpenNotes={() => {
            setNotesDraft(prev => ({ ...prev, [p.policyEntityId]: p.notes ?? '' }))
            setNotesOpenId(p.policyEntityId)
          }}
          onChangeNotes={value => setNotesDraft(prev => ({ ...prev, [p.policyEntityId]: value }))}
          onBlurNotes={() => handleNotesBlur(p)}
        />
      ),
    },
    {
      id: 'tested',
      header: 'Tested by',
      inlineOnPhone: true,
      cell: p =>
        p.testedBy || p.testedAt ? (
          <span className="text-helper">
            {p.testedBy}
            {p.testedAt && <span className="block text-text-helper">{new Date(p.testedAt).toLocaleDateString()}</span>}
          </span>
        ) : (
          <span className="text-helper text-text-helper">Not tested</span>
        ),
    },
  ]

  const renderDetail = (policy: QaPolicy) => (
    <RowDetail
      columns={[
        <>
          {policy.description && (
            <RowDetailSection title="Description" as="h4">
              {policy.description}
            </RowDetailSection>
          )}
          {policy.remediationSteps && (
            <RowDetailSection title="Remediation" as="h4">
              {policy.remediationSteps}
            </RowDetailSection>
          )}
        </>,
        <>
          {policy.powerShellEquivalent && (
            <RowDetailSection title="PowerShell" as="h4">
              <CodeSnippet kind="single" code={policy.powerShellEquivalent} language="PowerShell" />
            </RowDetailSection>
          )}
          {policy.remediationUrl && (
            <RowDetailSection title="Microsoft documentation" as="h4">
              <a
                href={policy.remediationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-start gap-1 text-link [overflow-wrap:anywhere] hover:text-link-hover hover:underline"
              >
                {policy.remediationUrl}
                <IconExternal className="mt-0.5 flex-none" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </RowDetailSection>
          )}
        </>,
      ]}
    />
  )

  return (
    <DataTable
      id="qa-policies"
      className="mt-12 scroll-mt-4"
      title="Policies"
      description="A shared checklist for confirming each Best Practice policy behaves as written."
      columns={columns}
      rows={policies}
      rowId={p => p.policyEntityId}
      rowLabel={p => p.policyId}
      size="md"
      isLoading={isLoading}
      toolbar={toolbar}
      expandable={{ render: renderDetail, isExpandable: hasDetail }}
      groups={{
        key: productOf,
        header: (product, members) => (
          <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span>{product}</span>
            <span className="text-helper font-normal text-text-helper">
              {members.length} {members.length === 1 ? 'policy' : 'policies'}
            </span>
            <span className="flex flex-wrap gap-1 font-normal c-md:ml-auto">
              <GroupStatusBadges policies={members} />
            </span>
          </span>
        ),
        expandedGroups,
        onExpandedGroupsChange,
      }}
      empty={
        <EmptyState align="center" headline="No policies match these filters." body="Clear the search or choose All in a filter to see more." />
      }
    />
  )
}

const LOAD_FAILED_BRIEF: ActionBrief = {
  headline: 'Reload the page to see the checklist.',
  lede: '**The policy checklist did not load**, so Guardian cannot show what is tested.',
}

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Untested', label: 'Untested' },
  { value: 'Pass', label: 'Pass' },
  { value: 'Fail', label: 'Fail' },
  { value: 'Investigate', label: 'Investigate' },
]

export default function AdminQaValidation() {
  const { accounts } = useMsal()
  const testedByName = accounts[0]?.name ?? accounts[0]?.username ?? 'Unknown'

  const { data, isLoading, error, refetch, isFetching } = useQaValidation()

  const [search, setSearch] = useState('')
  const [filterProduct, setFilterProduct] = useState('')
  const [filterCriticality, setFilterCriticality] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  // Products the admin closed; every other group starts and stays open. Remembered across filters, so a
  // product closed, filtered out and shown again comes back closed.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  const filtered = useMemo(() => {
    if (!data) return []
    return data.policies.filter(p => {
      if (filterProduct && p.product !== filterProduct) return false
      if (filterCriticality && p.criticality !== filterCriticality) return false
      if (filterStatus && p.status !== filterStatus) return false
      if (search) {
        const q = search.toLowerCase()
        return (
          p.policyId.toLowerCase().includes(q) ||
          p.title.toLowerCase().includes(q) ||
          (p.category ?? '').toLowerCase().includes(q)
        )
      }
      return true
    })
  }, [data, search, filterProduct, filterCriticality, filterStatus])

  const grouped = useMemo(() => {
    const map = new Map<string, QaPolicy[]>()
    for (const product of PRODUCTS) map.set(product, [])
    for (const p of filtered) {
      const key = productOf(p)
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(p)
    }
    // Remove empty groups
    for (const [key, policies] of map) {
      if (policies.length === 0) map.delete(key)
    }
    return map
  }, [filtered])

  // DataTable groups rows in the order they arrive, so the rows go in product order.
  const ordered = useMemo(() => Array.from(grouped.values()).flat(), [grouped])
  const products = Array.from(grouped.keys())
  const expandedGroups = products.filter(p => !collapsedGroups.has(p))
  const handleExpandedGroupsChange = (open: string[]) =>
    setCollapsedGroups(prev => {
      const next = new Set([...prev].filter(p => !products.includes(p)))
      for (const p of products) if (!open.includes(p)) next.add(p)
      return next
    })

  const pct = data && data.total > 0 ? Math.round((data.tested / data.total) * 100) : 0

  const brief: ActionBrief = error
    ? LOAD_FAILED_BRIEF
    : composeQaBrief({
        total: data?.total ?? 0,
        tested: data?.tested ?? 0,
        fail: data?.fail ?? 0,
        investigate: data?.investigate ?? 0,
      })

  // The primary narrows the list to the policies the headline names.
  const focus: { status: QaStatus; label: string } | null = !data
    ? null
    : data.fail > 0
      ? { status: 'Fail', label: 'Show the failing policies' }
      : data.investigate > 0
        ? { status: 'Investigate', label: 'Show the flagged policies' }
        : data.total - data.tested > 0
          ? { status: 'Untested', label: 'Show the untested policies' }
          : null

  const showFocus = () => {
    if (!focus) return
    setSearch('')
    setFilterProduct('')
    setFilterCriticality('')
    setFilterStatus(focus.status)
    requestAnimationFrame(() => document.getElementById('qa-policies')?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
  }

  return (
    <div>
      <section aria-labelledby="qa-verdict" className="mt-8">
        <BriefHero
          brief={brief}
          size="pillar"
          headingId="qa-verdict"
          isLoading={isLoading}
          provenance={data ? `${data.tested} of ${data.total} policies tested by your team` : undefined}
          secondaryAction={
            error ? (
              <Button kind="tertiary" onClick={() => refetch()}>
                Retry
              </Button>
            ) : data ? (
              <>
                {focus && (
                  <Button kind="primary" icon={<IconArrowRight />} onClick={showFocus}>
                    {focus.label}
                  </Button>
                )}
                <Button kind="tertiary" icon={<IconDownload />} onClick={() => downloadCsv(data.policies)}>
                  Export CSV
                </Button>
              </>
            ) : undefined
          }
        />
      </section>

      {error ? (
        <InlineNotification kind="error" title="Policy validation did not load." className="mt-12">
          {(error as Error).message}
        </InlineNotification>
      ) : (
        <>
          {/* Progress + Summary Cards */}
          <CondensedGrid as="section" columns={{ base: 1, 'c-md': 2, 'c-xlg': 4 }} className="mt-8" aria-label="Checklist at a glance">
            <Tile>
              <TileHeader title="Tested" />
              {data ? (
                <>
                  <BigNumber value={data.tested} of={data.total} size="figure-lg" className="mt-4" />
                  <ProgressBar value={data.tested} max={data.total} label={`${pct}% of the checklist tested`} hideLabel className="mt-4" />
                </>
              ) : (
                <Skeleton lines={2} className="mt-4" />
              )}
            </Tile>
            <Tile>
              <TileHeader title="Pass" />
              <BigNumber value={data?.pass ?? null} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">Behave as written.</p>
            </Tile>
            <Tile>
              <TileHeader title="Fail" />
              <BigNumber value={data?.fail ?? null} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">Do not behave as written.</p>
            </Tile>
            <Tile>
              <TileHeader title="Investigate" />
              <BigNumber value={data?.investigate ?? null} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">Need a closer look.</p>
            </Tile>
          </CondensedGrid>

          <PolicyChecklist
            policies={ordered}
            testedByName={testedByName}
            expandedGroups={expandedGroups}
            onExpandedGroupsChange={handleExpandedGroupsChange}
            isLoading={isLoading}
            toolbar={
              <>
                <TableSearch value={search} onChange={setSearch} label="Search policy ID, title or category" placeholder="Search policies" />
                <TableSelect
                  label="Product"
                  value={filterProduct}
                  onChange={setFilterProduct}
                  options={[{ value: '', label: 'All' }, ...PRODUCTS.map(p => ({ value: p, label: p }))]}
                />
                <TableSelect
                  label="Criticality"
                  value={filterCriticality}
                  onChange={setFilterCriticality}
                  options={[{ value: '', label: 'All' }, ...CRITICALITIES.map(c => ({ value: c, label: SEVERITY_WORD[severityFromApi(c)] }))]}
                />
                <TableSelect label="Status" value={filterStatus} onChange={setFilterStatus} options={STATUS_OPTIONS} />
                <Button kind="ghost" onClick={() => refetch()} disabled={isFetching} icon={isFetching ? <Spinner /> : undefined} className="flex-none">
                  Refresh
                </Button>
              </>
            }
          />
        </>
      )}
    </div>
  )
}
